import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import axios from "axios";
import { BASEURL } from "../Config";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import { useHistory } from "react-router-dom";
import searchIcon from "../assets/search_icon.png";
import downloadIcon from "../assets/download_icon.png";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

const SalesDailyAttendanceReport = () => {
    const history = useHistory();
    const [attendanceData, setAttendanceData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [offset, setOffset] = useState(0);
    const [slice, setSlice] = useState([]);
    const perPage = 10;
    const [pageCount, setPageCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
    const [noResults, setNoResults] = useState(false);

    // Statistics
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [present, setPresent] = useState(0);
    const [absent, setAbsent] = useState(0);
    const [eventsCompleted, setEventsCompleted] = useState(0);

    useEffect(() => {
        fetchAttendanceData();
    }, [startDate]);

    const fetchAttendanceData = () => {
        axios.get(`${BASEURL}/api/v1/salesManagerAttendance/dailyReport?date=${startDate}`)
            .then(res => {
                if (res.status === 200) {
                    setAttendanceData(res.data);
                    setFilteredData(res.data);
                    const slice = res.data.slice(offset, offset + perPage);
                    setSlice(slice);
                    setPageCount(Math.ceil(res.data.length / perPage));

                    // Calculate statistics
                    setTotalEmployees(res.data.length); 
                    setPresent(res.data.filter(item => item.attendance === 'Present' || item.dayStartTime !== '-').length);
                    setAbsent(res.data.filter(item => item.attendance === 'Absent' && item.dayStartTime === '-').length);
                    setEventsCompleted(res.data.reduce((sum, item) => sum + item.eventsCompleted, 0));
                } else {
                    setNoResults(true);
                }
            })
            .catch(err => {
                console.error("Error fetching attendance data:", err);
                setNoResults(true);
            });
    };

    const handlePageClick = (e) => {
        const selectedPage = e.selected;
        const newOffset = selectedPage * perPage;
        setCurrentPage(selectedPage);
        setOffset(newOffset);
        sliceData(newOffset);
    };

    const sliceData = (newOffset) => {
        const sliceToSet = filteredData.slice(newOffset, newOffset + perPage);
        setSlice(sliceToSet);
    };

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            handleSearch();
        }, 500); // 500ms debounce

        return () => clearTimeout(debounceTimeout); // Cleanup timeout on change
    }, [searchTerm, attendanceData]);

    const handleSearch = () => {
        if (searchTerm) {
            const results = attendanceData.filter(item =>
                item.salesManagerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.phoneNumber.includes(searchTerm)
            );
            setFilteredData(results);
            setPageCount(Math.ceil(results.length / perPage));
            setSlice(results.slice(0, perPage));
            setCurrentPage(0);
        } else {
            if (searchTerm !== '') {
                setNoResults(true);
            } else {
                handleShowAll();
            }
        }
    };

    const handleShowAll = () => {
        setFilteredData(attendanceData);
        setPageCount(Math.ceil(attendanceData.length / perPage));
        setSlice(attendanceData.slice(0, perPage));
        setCurrentPage(0);
        setSearchTerm('');
    };

    const formatTimeTo12Hour = (timestamp) => {
        const date = new Date(timestamp);
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12; // Convert to 12-hour format and handle midnight (0 becomes 12)
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes; // Add leading zero if needed
        return `${hours}:${formattedMinutes} ${ampm}`;
    };

    const downloadExcel = () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Daily Attendance Report");
    
        // Add headers
        worksheet.columns = [
            { header: "SNo", key: "sno", width: 5 },
            { header: "Sales Manager Name", key: "salesManagerName", width: 25 },
            { header: "Phone Number", key: "phoneNumber", width: 15 },
            { header: "Attendance", key: "attendance", width: 10 },
            { header: "Day Start Time", key: "dayStartTime", width: 20 },
            { header: "Day End Time", key: "dayEndTime", width: 20 },
            { header: "Events Completed", key: "eventsCompleted", width: 15 },
        ];
    
        // Format data
        const dataToExport = searchTerm ? filteredData : attendanceData;
        dataToExport.forEach((item, index) => {
            const row = worksheet.addRow({
                sno: index + 1,
                salesManagerName: item.salesManagerName,
                phoneNumber: item.phoneNumber,
                attendance: item.dayStartTime !== '-' ? "Present" : item.attendance,
                dayStartTime: item.dayStartTime !== '-' ? formatTimeTo12Hour(item.dayStartTime) : item.dayStartTime,
                dayEndTime: item.dayEndTime !== '-' ? formatTimeTo12Hour(item.dayEndTime) : item.dayEndTime,
                eventsCompleted: item.eventsCompleted,
            });
    
            // Apply left alignment to all cells in the row
            row.eachCell((cell) => {
                cell.alignment = { horizontal: "left" };
            });
        });
    
        // Style header row
        worksheet.getRow(1).font = { bold: true };
    
        // Export workbook
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer]), `DailyAttendanceReport-${startDate}.xlsx`);
        });
    };

    return (
        <div id="wrapper">
            <Sidebar act="salesDailyReport" />
            <div className="content-page">
                <div className="content">
                    <Topbar act="Daily Report" breadcrumbItems={['Sales App', 'Daily Report']} />
                    
                    <div className="container-fluid reports-container">
                        <div className="card sales-report-card">
                            <div className="card-body">

                                <div className="row stats-row">
                                    <div className="col-md-3">
                                        <div className="card card1">
                                            <div className="card-body">
                                                <h3>{totalEmployees}</h3>
                                                <h5>Total Employees</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card card2">
                                            <div className="card-body">
                                                <h3>{present}</h3>
                                                <h5>Present</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card card3">
                                            <div className="card-body">
                                                <h3>{absent}</h3>
                                                <h5>Absent</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card card4">
                                            <div className="card-body">
                                                <h3>{eventsCompleted}</h3>
                                                <h5>Places Visited</h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div>
                                        <form>
                                            <div className="d-flex mt-2 align-items-center justify-content-between">
                                                {/* Search Input and Date Picker */}
                                                <div className="d-flex">
                                                    <div className="col-md-8 position-relative me-3">
                                                        <div className="input-group">
                                                            <span className="input-group-text">
                                                                <img
                                                                    src={searchIcon}
                                                                    alt="search"
                                                                    className="search-icon"
                                                                />
                                                            </span>
                                                            <input
                                                                type="text"
                                                                id="search"
                                                                className="form-control search-input"
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                                placeholder="Search By Name or Phone"
                                                                value={searchTerm}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <input
                                                            type="date"
                                                            value={startDate}
                                                            className="form-control date-picker"
                                                            onChange={(e) => {
                                                                console.log("value", e.target.value);
                                                                setStartDate(e.target.value)
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Icons Section */}
                                                <div className="d-flex align-items-center">
                                                    <button type="button" className="btn btn-icon btn-margin-right" onClick={downloadExcel}>
                                                        <img src={downloadIcon} alt="Download" className="icon-img" />
                                                    </button>
                                                    {/* <button type="button" className="btn btn-icon btn-margins">
                                                        <img src={sortIcon} alt="Sort" className="icon-img" />
                                                    </button>
                                                    <button type="button" className="btn btn-icon btn-margin-left">
                                                        <img src={filterIcon} alt="Filter" className="icon-img" />
                                                    </button> */}
                                                </div>
                                            </div>
                                        </form>
                                    </div>

                                    {/* {noResults ? <p>No results found</p> : ''} */}
                                    {/* end row */}
                                    <table className="table table-centered mb-0 mt-2 report-table">
                                        <thead>
                                            <tr>
                                                <th>SNo</th>
                                                <th>Sales Manager Name</th>
                                                <th>Phone Number</th>
                                                <th>Attendance</th>
                                                <th>Day Start Time</th>
                                                <th>Day End Time</th>
                                                <th>Events Completed</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        {noResults ?
                                            <tbody>
                                                <tr className="text-center">
                                                    <td colSpan={7}>
                                                        No Results Found
                                                    </td>
                                                </tr>
                                            </tbody>
                                            :
                                            <tbody>
                                                {slice.map((data, idx) => (
                                                    <tr key={data.salesManagerUUID}>
                                                        <td>{currentPage * perPage + idx + 1}</td>
                                                        <td className="table-user">{data.salesManagerName}</td>
                                                        <td>{data.phoneNumber}</td>
                                                        <td><span className={(data.attendance === "Present" || data.dayStartTime !== '-') ? "present attendance-status" : "absent attendance-status"}>{data.dayStartTime !== '-' ? "Present" : data.attendance}</span></td>
                                                        <td>{data.dayStartTime !== '-' ? formatTimeTo12Hour(data.dayStartTime): data.dayStartTime }</td>
                                                        <td>{data.dayEndTime !== '-' ? formatTimeTo12Hour(data.dayEndTime) : data.dayEndTime }</td>
                                                        <td>{data.eventsCompleted}</td>
                                                        <td>
                                                            <button className="btn viewIcon"
                                                                onClick={() => { history.push('viewSalesManager/' + data.salesManagerUUID) }}>
                                                                <i className="mdi mdi-chevron-right" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        }
                                    </table>

                                    {!noResults && (
                                        <div className="pagination-div">
                                            <ReactPaginate
                                                previousLabel={<i className="mdi mdi-chevron-left" />}
                                                nextLabel={<i className="mdi mdi-chevron-right" />}
                                                breakLabel={"..."}
                                                breakClassName={"break-me"}
                                                pageCount={pageCount}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePageClick}
                                                forcePage={currentPage}
                                                containerClassName={"pagination"}
                                                subContainerClassName={"pages pagination"}
                                                activeClassName={"active"} />
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
};

export default SalesDailyAttendanceReport;
